/**
 * Account-wide alternate explorer art for client UI (Strategy tooltips, OrderDesign, action log, etc.).
 * Populated from GET /explore-unknown/progress; updated when the user toggles art on Strategy.
 */

/** @type {Record<string, number>} */
let prefsByOrderId = {};
/** @type {Set<number>} */
let unlockedOrderIds = new Set();
/** @type {Record<string, number[]>} */
let titleUnlockedVariantsByOrderId = {};

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizeArtVariantPrefValue(raw) {
  if (raw === true) return 1;
  if (raw === false || raw == null) return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : 0;
}

/**
 * @param {Record<string, number|boolean>|null|undefined} prefs
 * @param {Set<number>|number[]|null|undefined} unlocked
 * @param {Record<string, number[]>|null|undefined} [titleVariants]
 */
export function setAlternateArtDisplayContext(prefs, unlocked, titleVariants) {
  const nextPrefs = {};
  if (prefs && typeof prefs === 'object') {
    for (const [key, val] of Object.entries(prefs)) {
      nextPrefs[key] = normalizeArtVariantPrefValue(val);
    }
  }
  prefsByOrderId = nextPrefs;
  if (unlocked instanceof Set) {
    unlockedOrderIds = new Set(unlocked);
  } else if (Array.isArray(unlocked)) {
    unlockedOrderIds = new Set(unlocked.map(Number).filter(Number.isInteger));
  } else {
    unlockedOrderIds = new Set();
  }
  titleUnlockedVariantsByOrderId =
    titleVariants && typeof titleVariants === 'object' ? { ...titleVariants } : {};
}

export function clearAlternateArtDisplayContext() {
  setAlternateArtDisplayContext({}, new Set(), {});
}

/**
 * @param {number|string} orderId
 * @param {number} artVariant
 */
export function patchAlternateArtDisplayPref(orderId, artVariant) {
  prefsByOrderId = {
    ...prefsByOrderId,
    [String(orderId)]: normalizeArtVariantPrefValue(artVariant),
  };
}

/**
 * @param {unknown} orderId
 * @returns {number[]}
 */
export function getUnlockedArtVariantsForOrder(orderId) {
  const id = Number(orderId);
  if (!Number.isInteger(id)) return [0];
  const titleVariants = titleUnlockedVariantsByOrderId[String(id)];
  if (Array.isArray(titleVariants) && titleVariants.length > 0) {
    return [...titleVariants].sort((a, b) => a - b);
  }
  if (unlockedOrderIds.has(id)) {
    return [0, 1];
  }
  return [0];
}

/**
 * @param {unknown} orderId
 * @returns {boolean}
 */
export function isAlternateArtUnlocked(orderId) {
  return getUnlockedArtVariantsForOrder(orderId).length > 1;
}

/**
 * @param {unknown} orderId
 * @returns {number}
 */
export function getOrderArtVariant(orderId) {
  return normalizeArtVariantPrefValue(prefsByOrderId[String(orderId)]);
}

/**
 * @param {unknown} orderId
 * @returns {boolean}
 */
export function orderUsesAlternateArt(orderId) {
  return getOrderArtVariant(orderId) > 0;
}

/**
 * @param {unknown} orderId
 * @param {number} variantIndex
 * @returns {string|null}
 */
export function resolveArtVariantImageSrc(orderId, variantIndex) {
  const id = Number(orderId);
  const variant = Number(variantIndex);
  if (!Number.isInteger(id) || !Number.isInteger(variant) || variant === 0) {
    return null;
  }
  const unlocked = getUnlockedArtVariantsForOrder(id);
  if (!unlocked.includes(variant)) {
    return null;
  }
  if (variant === 1 && unlockedOrderIds.has(id) && !titleUnlockedVariantsByOrderId[String(id)]) {
    return `/images/a${id}.jpg`;
  }
  if (id === 402) {
    if (variant === 1) return '/images/a402.jpg';
    if (variant === 2) return '/images/a402_2.jpg';
  }
  if (variant === 1 && unlockedOrderIds.has(id)) {
    return `/images/a${id}.jpg`;
  }
  return null;
}

/**
 * @param {unknown} orderId
 * @returns {string|null}
 */
export function resolveAlternateArtImageSrc(orderId) {
  const variant = getOrderArtVariant(orderId);
  return resolveArtVariantImageSrc(orderId, variant);
}

/**
 * @param {object|null|undefined} orderLike
 * @returns {object|null|undefined}
 */
export function withAlternateArtDisplayOrder(orderLike) {
  if (!orderLike || orderLike.id == null) return orderLike;
  if (orderLike.image && orderLike.useAlternateArt === true && orderLike.alternateArtVariant != null) {
    return orderLike;
  }
  const variant = getOrderArtVariant(orderLike.id);
  if (variant === 0) return orderLike;
  const image = resolveArtVariantImageSrc(orderLike.id, variant);
  if (!image) return orderLike;
  return {
    ...orderLike,
    useAlternateArt: true,
    alternateArtVariant: variant,
    image,
  };
}

/**
 * @param {string} apiUrl
 * @param {string} token
 * @returns {Promise<{ prefs: Record<string, number>, unlocked: Set<number>, titleVariants: Record<string, number[]> }>}
 */
export async function loadAlternateArtDisplayContextFromProgress(apiUrl, token) {
  if (!apiUrl) {
    throw new Error('loadAlternateArtDisplayContextFromProgress: apiUrl is required');
  }
  if (!token) {
    throw new Error('loadAlternateArtDisplayContextFromProgress: token is required');
  }
  const res = await fetch(`${apiUrl}/explore-unknown/progress`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`loadAlternateArtDisplayContextFromProgress: HTTP ${res.status}`);
  }
  const data = await res.json();
  const defs = data.exploreUnknownLevelDefinitions || {};
  const level = Number(data.exploreUnknownLevel) || 1;
  const unlocked = new Set(
    (data.exploreUnknownAlternateArtUnlockedOrderIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id))
  );
  if (unlocked.size === 0) {
    for (const def of Object.values(defs)) {
      if (def.type === 'loreArt' && level >= def.level && def.effects?.orderId != null) {
        unlocked.add(Number(def.effects.orderId));
      }
    }
  }
  const prefsRaw =
    data.exploreUnknownAlternateArtPrefs && typeof data.exploreUnknownAlternateArtPrefs === 'object'
      ? data.exploreUnknownAlternateArtPrefs
      : {};
  const prefs = {};
  for (const [key, val] of Object.entries(prefsRaw)) {
    prefs[key] = normalizeArtVariantPrefValue(val);
  }
  const titleVariants =
    data.exploreUnknownTitleAlternateArtUnlockedVariantsByOrderId &&
    typeof data.exploreUnknownTitleAlternateArtUnlockedVariantsByOrderId === 'object'
      ? data.exploreUnknownTitleAlternateArtUnlockedVariantsByOrderId
      : {};
  setAlternateArtDisplayContext(prefs, unlocked, titleVariants);
  return { prefs, unlocked, titleVariants };
}
